
import React, { useState, useRef, useEffect } from 'react';
import { Spin } from 'antd';
import {
  FilePdfOutlined,
  FileWordOutlined,
  FileExcelOutlined,
  FileTextOutlined,
  FileOutlined,
  FileImageOutlined
} from '@ant-design/icons';
import logger from '../utils/logger';

/**
 * مكون لعرض المصغرات لجميع أنواع الملفات
 * للصور: يستخدم Canvas لإنشاء مصغرة
 * للملفات الأخرى: يعرض أيقونة مناسبة
 */
const ImageThumbnail = ({ src, alt, onError, onClick, width = '100%', height = '120px', documentType = '' }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  // تحديد أيقونة حسب نوع الملف
  const getFileIcon = (type) => {
    if (!type) return <FileOutlined />;
    const lowerType = type.toLowerCase();
    
    if (lowerType.includes('pdf')) {
      return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    } else if (lowerType.includes('word') || lowerType.includes('docx') || lowerType.includes('doc')) {
      return <FileWordOutlined style={{ color: '#1890ff' }} />;
    } else if (lowerType.includes('excel') || lowerType.includes('xlsx') || lowerType.includes('xls')) {
      return <FileExcelOutlined style={{ color: '#52c41a' }} />;
    } else if (lowerType.includes('text') || lowerType.includes('txt') || lowerType.includes('json') || 
               lowerType.includes('xml') || lowerType.includes('javascript') || lowerType.includes('css') || 
               lowerType.includes('html')) {
      return <FileTextOutlined style={{ color: '#666' }} />;
    } else if (lowerType.includes('image')) {
      return <FileImageOutlined style={{ color: '#722ed1' }} />;
    } else {
      return <FileOutlined style={{ color: '#666' }} />;
    }
  };

  // تحديد نوع الملف
  const isImage = documentType.toLowerCase().includes('image');
  const isText = documentType.toLowerCase().includes('text') || 
                documentType.toLowerCase().includes('json') ||
                documentType.toLowerCase().includes('xml') ||
                documentType.toLowerCase().includes('javascript') ||
                documentType.toLowerCase().includes('css') ||
                documentType.toLowerCase().includes('html');

  useEffect(() => {
    // إذا لم يكن صورة، لا نحتاج لمعالجة المصغرة
    if (!isImage) {
      setLoading(false);
      return;
    }

    if (!src) return;

    const img = new Image();
    img.crossOrigin = 'anonymous'; // للسماح بتحميل الصور من مصادر مختلفة
    imgRef.current = img;

    img.onload = () => {
      try {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // حساب أبعاد الصورة المصغرة مع الحفاظ على نسبة العرض إلى الارتفاع
        const canvasWidth = canvas.offsetWidth || 300;
        const canvasHeight = canvas.offsetHeight || 120;

        // حساب نسبة التصغير
        const scale = Math.min(
          canvasWidth / img.width,
          canvasHeight / img.height
        );

        const thumbWidth = img.width * scale;
        const thumbHeight = img.height * scale;

        // تعيين أبعاد الـ canvas
        canvas.width = thumbWidth;
        canvas.height = thumbHeight;

        // رسم الصورة المصغرة
        ctx.drawImage(img, 0, 0, thumbWidth, thumbHeight);

        setLoading(false);
      } catch (e) {
        logger.error('Error creating thumbnail:', e);
        setError(true);
        setLoading(false);
        if (onError) onError();
      }
    };

    img.onerror = () => {
      setError(true);
      setLoading(false);
      
      // محاولة تحميل الصورة بدون التوكن كخيار أخير
      if (src.includes('token=')) {
        // إذا كان الرابط يحتوي بالفعل على توكن، جرب إزالته
        const urlWithoutToken = src.split('token=')[0].slice(0, -1);
        img.src = urlWithoutToken;
      } else {
        // محاولة تحميل الصورة الأصلية
        img.src = src.split('?')[0];
      }
      
      if (onError) onError();
    };

    // استخدام fetch لتحميل الصورة مع التوكن في رأس الطلب
    const token = localStorage.getItem("token") || sessionStorage.getItem("token");
    
    if (token) {
      // استخدام fetch لتحميل الصورة مع التوكن في رأس الطلب
      fetch(src, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.blob();
      })
      .then(blob => {
        const url = URL.createObjectURL(blob);
        img.src = url;
        // تنظيف الرابط عند إلغاء تحميل المكون
        return () => URL.revokeObjectURL(url);
      })
      .catch(error => {
        logger.error("Error loading image with auth:", error);
        
        // محاولة بديلة: استخدام التوكن كمعامل في الرابط
        const urlWithToken = `${src}${src.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`;
        img.src = urlWithToken;
        
        // إذا فشلت المحاولة البديلة أيضاً، جرب بدون توكن
        img.onerror = () => {
          logger.debug("Failed with token parameter, trying without token");
          const urlWithoutToken = src.split('?')[0];
          img.src = urlWithoutToken;
          
          // إذا فشلت المحاولة الأخيرة، قم بتعيين الخطأ
          img.onerror = () => {
            setError(true);
            setLoading(false);
            if (onError) onError();
          };
        };
      });
    } else {
      img.src = src;
    }

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [src, onError]);

  return (
    <div
      className="image-thumbnail-container"
      style={{
        position: 'relative',
        width,
        height,
        borderRadius: '8px',
        overflow: 'hidden',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '2px solid #e8e8e8',
        backgroundColor: '#f5f5f5',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'scale(1.05)';
        e.currentTarget.style.borderColor = '#1890ff';
        e.currentTarget.style.boxShadow = '0 8px 16px rgba(24,144,255,0.2)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'scale(1)';
        e.currentTarget.style.borderColor = '#e8e8e8';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {loading && (
        <Spin size="small" />
      )}

      {error ? (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            color: '#999',
            fontSize: '12px',
            textAlign: 'center',
            padding: '8px'
          }}
        >
          فشل تحميل الصورة
        </div>
      ) : isImage ? (
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: '100%',
            maxHeight: '100%',
            display: loading ? 'none' : 'block'
          }}
          alt={alt}
        />
      ) : (
        <div
          style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '8px',
            fontSize: '40px'
          }}
        >
          {getFileIcon(documentType)}
          <div style={{
            fontSize: '12px',
            marginTop: '8px',
            textAlign: 'center',
            color: '#666',
            maxWidth: '90%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {alt}
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageThumbnail;
